
import {
  computed,
  defineComponent, ref, SetupContext, Ref
} from 'vue';
import Dropdown from 'primevue/dropdown';
import Tooltip from 'primevue/tooltip';
import OverlayPanel from 'primevue/overlaypanel';
import OverrideInfoPanel from '@/components/segmentations/OverrideInfoPanel.vue';
import { useI18n } from "vue-i18n";
import { store } from "@/store";
import validateSegmentProperty from '@/components/segmentations/utils/utils';
import useProductOverrides from '@/composables/segmentations/useProductOverrides';
import { isAwaitedVisitDuration } from '@/composables/segmentations/triggerSegmentation';

export default defineComponent({
  name: 'SegmentationComplexPropertyDropdown',

  components: { Dropdown, OverlayPanel, OverrideInfoPanel },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    propertyConfig: {
      type: Object,
      required: true,
    },

    propertyDefinition: {
      type: Object,
      required: true,
    },

    property: {
      type: String,
      required: true
    },

    index: {
      type: Number,
      required: true,
    }
  },

  emits: ['update:property', 'reset-property-input'],


  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();
    const subPropertyId = ref(props.property);

    const optionDisabled = ref('');
    const overridePanelRef = ref();
    const currentOverrideField = ref<string | null>(null);
    const { loadOverrides, hasOverrides } = useProductOverrides();

    const subPropertiesOptions = computed(() =>
      Object.entries(props.propertyDefinition.sub_properties).map(([key, values]: [string, any]) => {
        const config = props.propertyConfig.sub_properties.filter((property: any) => property.id);
        const isMultiple = 'multiple' in values && values.multiple;
        const disabled = config.find((property: any) => property.id === values.id);
        return {
          label: t(values.label),
          value: values.id,
          disabled: disabled !== undefined && !isMultiple && disabled.id !== subPropertyId.value,
          isCustomAttribute: values.isCustomAttribute || false,
          isAwaited: isAwaitedVisitDuration(props.propertyDefinition.id, values.id),
        };
      })
    );

    const currentSegmentConfig = computed(() => store.getters['segmentationsEditor/getCurrentSegmentConfig']);

    // Helper function to check if selected option is custom
    const getSelectedOptionIsCustom = (value: string) => {
      const option = subPropertiesOptions.value.find(opt => opt.value === value);
      return option?.isCustomAttribute || false;
    };

    // Helper function to get selected option label
    const getSelectedOptionLabel = (value: string) => {
      const option = subPropertiesOptions.value.find(opt => opt.value === value);
      return option?.label || '';
    };

    const refreshComplexPropertyComponent = () => {
      emit('refreshComponent');
    }

    const resetPropertyInput = () => {
      emit('update:property', subPropertyId.value);
      emit('reset-property-input');
    }

    const dropdownRef = ref();
    const onBeforeShow = async () => {
      optionDisabled.value = 'disabled';
      if (currentSegmentConfig.value) {
        if (currentSegmentConfig.value.configProperty.segmentId !== props.propertyConfig.sub_properties[props.index].segmentId) {
          const { segmentId } = currentSegmentConfig.value.configProperty;
          const validation = await validateSegmentProperty(currentSegmentConfig.value.configProperty, currentSegmentConfig.value.propertyDefinition);
          if (validation && !validation.success) {
            store.commit('segmentationsEditor/pushError', { type: 'segments', value: { segmentId, value: validation.validate.value } });
            dropdownRef.value.hide();
            return false;
          }
          store.commit('segmentationsEditor/removeError', { type: 'segments', id: 'segmentId', value: segmentId });
          store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
          store.commit('segmentationsEditor/setRefreshContactsNumber', true);
        }
      } else {
        store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
      }
    };

    const onBeforeHide = () => {
      optionDisabled.value = '';
    };

    /**
     * Check if a property is overridden
     *
     * @param propertyId - The ID of the property to check
     * @returns true if the property is overridden, false otherwise
     */
    const isPropertyOverridden = (propertyId: string) => {
      return hasOverrides(propertyId, props.propertyConfig.id);
    };

    const openOverridePanel = (event: Event, propertyId: string) => {
      currentOverrideField.value = propertyId;
      overridePanelRef.value.toggle(event);
    };

    return {
      t,
      subPropertiesOptions,
      dropdownRef,
      subPropertyId,
      optionDisabled,
      resetPropertyInput,
      refreshComplexPropertyComponent,
      onBeforeShow,
      onBeforeHide,
      getSelectedOptionIsCustom,
      getSelectedOptionLabel,
      isAwaitedVisitDuration,
      isPropertyOverridden,
      overridePanelRef,
      openOverridePanel,
      currentOverrideField,
    };
  },
});
